<template>
  <section class="section">
    <h2 class="title">
      {{ $t('route.sourceTypes') }}
    </h2>
    <div class="level">
      <div class="level-left subtitle">
        {{ $t('subtitle.sourceTypes') }}
      </div>
      <div class="level-right">
        <div class="field is-grouped">
          <div class="control">
            <button
              class="button is-primary"
              @click="showFilter = true"
              :class="{ 'is-info': hasActiveFilters }"
            >
              <octicon :icon="search" />
              <span>{{ $t('users.filter.title') }}</span>
              <span v-if="hasActiveFilters" class="tag is-light ml-1">{{ activeFilterCount }}</span>
            </button>
          </div>
          <div class="control">
            <button class="button is-primary" @click.prevent="create">
              <octicon :icon="plus" /><span>{{ $t('newSourceType') }}</span>
            </button>
          </div>
          <div class="control">
            <button class="button is-info" @click.prevent="openParserV2Wizard">
              <octicon :icon="zap" /><span>{{ $t('sourceTypes.wizard.button') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <box>
      <data-table
        ref="table"
        :data="tableData"
        :columns="tableColumns"
        :loading="isTableLoading"
        :searchable="true"
        :search-placeholder="$t('sourceTypes.searchPlaceholder')"
        :items-per-page-options="[10, 15, 30, 50]"
        :default-items-per-page="10"
        :default-sort-by="'created_at'"
        :default-sort-order="'desc'"
        :server-side="true"
        :total-items="totalItems"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      >
        <!-- STT Column -->
        <template #cell-stt="{ index }">
          {{ index + 1 }}
        </template>

        <!-- Name Column -->
        <template #cell-name="{ value }">
          <span class="truncate-text" :title="value">{{ value }}</span>
        </template>

        <!-- Description Column -->
        <template #cell-description="{ value }">
          <span class="truncate-text" :title="value">{{ value || '-' }}</span>
        </template>

        <!-- Type Column -->
        <template #cell-type="{ value }">
          <span class="truncate-text" :title="formatType(value)">{{ formatType(value) }}</span>
        </template>

        <!-- Settings Column -->
        <template #cell-settings="{ row }">
          <tabs class="settings-tabs" :theme="$_ui_theme_tabs">
            <tab :title="$t('delimiter')">
              <pre class="settings-pre">{{ row.auto_detect ? '(auto)' : escapeNL(row.event_delimiter) }}</pre>
            </tab>
            <tab :title="$t('comment')">
              <pre class="settings-pre">{{ row.auto_detect ? '(auto)' : row.comment }}</pre>
            </tab>
            <tab :title="$t('sourceTypes.form.timestampMode')">
              <pre class="settings-pre">{{ row.time_fill ? '(auto)' : row.time_field + `(${row.time_format})` }}</pre>
            </tab>
            <tab v-if="row.type === 'csv'" :title="$t('fields')">
              <pre class="settings-pre">{{ renderFields(row) }}</pre>
            </tab>
            <tab v-if="row.type === 'custom'" :title="$t('regexp')">
              <pre class="settings-pre">{{ escapeNL(row.regex_string) }}</pre>
            </tab>
          </tabs>
        </template>

        <!-- Created At Column -->
        <template #cell-created_at="{ value }">
          <span v-if="value">{{ formatDate(value) }}</span>
          <span v-else>-</span>
        </template>

        <!-- Actions Column -->
        <template #cell-actions="{ row }">
          <div class="has-text-centered">
            <button
              v-if="!row.is_system_type"
              class="button is-text"
              @click.prevent="edit(row)"
              :title="$t('edit')"
            >
              <octicon :icon="pencil" />
            </button>
            <button
              v-if="!row.is_system_type"
              class="button is-text"
              @click.prevent="destroy(row)"
              :title="$t('delete')"
            >
              <octicon :icon="x" />
            </button>
          </div>
        </template>
      </data-table>
    </box>

    <!-- Source Type Form Modal -->
    <source-type-form-modal
      :show="modal"
      :is-edit-mode="isEditMode"
      :edit-item="item"
      :existing-source-types="existingSourceTypes"
      @update:show="modal = $event"
      @success="handleFormSuccess"
    />

    <!-- Source Type Filter Modal -->
    <source-type-filter
      :show.sync="showFilter"
      :filters="currentFilters"
      @apply-filters="applyFilters"
    />

    <!-- Delete Confirmation Dialog -->
    <confirmation-dialog
      :show="showDeleteConfirmation"
      :title="deleteConfirmationTitle"
      :message="deleteConfirmationMessage"
      :warning-text="$t('sourceTypes.deleteConfirmation.warning')"
      :cancel-text="$t('sourceTypes.deleteConfirmation.cancel')"
      :confirm-text="$t('sourceTypes.deleteConfirmation.confirm')"
      confirm-button-class="is-danger"
      :show-countdown="true"
      :countdown-seconds="5"
      :countdown-message-template="$t('sourceTypes.deleteConfirmation.countdown')"
      :data-input-names="dependencyDataInputNames"
      :forwarder-names="dependencyForwarderNames"
      :dependency-warning-message="$t('sourceTypes.deleteConfirmation.dependencyWarning')"
      :data-input-label="$t('sourceTypes.deleteConfirmation.dataInputLabel')"
      :forwarder-label="$t('sourceTypes.deleteConfirmation.forwarderLabel')"
      @confirm="handleDeleteConfirm"
      @cancel="cancelDelete"
    />

    <!-- Dependency Warning Dialog -->
    <source-type-dependency-warning
      :show="showDependencyWarning"
      :data-input-names="dependencyDataInputNames"
      :forwarder-names="dependencyForwarderNames"
      @close="closeDependencyWarning"
    />

    <!-- ParserV2 Wizard (New) -->
    <parser-v2-wizard
      :show="showParserV2Wizard"
      :is-edit-mode="isParserV2EditMode"
      :edit-item="parserV2EditItem"
      @update:show="handleParserV2WizardClose"
      @success="handleWizardSuccess"
    />
  </section>
</template>
<script>
import DataTable from '@/components/common/DataTable.vue'
import { Tabs, Tab } from '@cyradar/ui'
import { plus, x, pencil, search, zap } from 'octicons-vue'
import SourceTypeFilter from '@/components/sourcetypes/SourceTypeFilter.vue'
import SourceTypeFormModal from '@/components/sourcetypes/SourceTypeFormModal.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import SourceTypeDependencyWarning from '@/components/SourceTypeDependencyWarning.vue'
import ParserV2Wizard from '@/components/sourcetypes/ParserV2Wizard.vue'

const uniq = (arr) => {
  return arr.filter((elem, pos, arr) => {
    return arr.indexOf(elem) === pos
  })
}

const types = ['json', 'csv', 'xml', 'syslog', 'cef', 'custom', 'rfc5424', 'linux_syslog', 'vcenter8', 'fortinet', 'f5_asm_key_value']

export default {
  components: {
    DataTable,
    Tabs,
    Tab,
    SourceTypeFilter,
    SourceTypeFormModal,
    ConfirmationDialog,
    SourceTypeDependencyWarning,
    ParserV2Wizard
  },
  data () {
    return {
      modal: false,
      showParserV2Wizard: false,
      isEditMode: false,
      isParserV2EditMode: false,
      parserV2EditItem: null,
      item: null,
      showFilter: false,
      currentFilters: {
        types: []
      },
      actualFilteredCount: 0, // Track actual number of filtered results
      validationErrors: {
        name: '',
        event_delimiter: '',
        field_delimiter: '',
        regex_string: '',
        csv_fields: '',
        time_field: '',
        time_format: ''
      },
      existingSourceTypes: [],
      isValidating: false,
      descriptionCharacterCount: 0,
      // Delete confirmation properties
      showDeleteConfirmation: false,
      itemToDelete: null,
      // Dependency warning properties
      showDependencyWarning: false,
      dependencyDataInputNames: [],
      dependencyForwarderNames: [],
      // DataTable properties
      tableData: [],
      isTableLoading: false,
      totalItems: 0,
      searchQuery: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    types () {
      return types
    },
    delimiter: {
      get () {
        if (!this.item || !this.item.event_delimiter) {
          return ''
        }

        return this.escapeNL(this.item.event_delimiter)
      },
      set (val) {
        const raw = this.unescapeNL(val)
        if (!this.item) {
          return
        }

        this.item.event_delimiter = raw
      }
    },
    fields () {
      if (!this.item) {
        return []
      }

      const names = this.item.field_names.split(',')

      return names.filter(name => !!name).map(name => ({
        name
      }))
    },
    /**
     * Table columns configuration
     */
    tableColumns() {
      return [
        {
          key: 'stt',
          label: this.$t('users.serialNumber'),
          field: 'stt',
          width: '3%',
          sortable: false,
          align: 'center'
        },
        {
          key: 'name',
          label: this.$t('sourceTypes.name'),
          field: 'name',
          width: '12%',
          sortable: true,
          truncate: true
        },
        {
          key: 'description',
          label: this.$t('description'),
          field: 'description',
          width: '12%',
          sortable: true,
          truncate: true
        },
        {
          key: 'type',
          label: this.$t('sourceTypes.format'),
          field: 'type',
          width: '7%',
          sortable: true,
          truncate: true
        },
        {
          key: 'settings',
          label: this.$t('settings'),
          field: 'settings',
          width: '25%',
          sortable: false
        },
        {
          key: 'created_at',
          label: this.$t('indexes.columnLabels.createdAt'),
          field: 'created_at',
          width: '8%',
          sortable: true
        },
        {
          key: 'actions',
          label: this.$t('actions'),
          field: 'actions',
          width: '10%',
          sortable: false,
          align: 'center'
        }
      ]
    },
    plus () {
      return plus
    },
    x () {
      return x
    },
    pencil () {
      return pencil
    },
    search () {
      return search
    },
    zap () {
      return zap
    },
    info () {
      return info
    },
    // Whether any filters are currently active
    hasActiveFilters () {
      return this.actualFilteredCount > 0
    },
    // Number of active filters - show the actual result count from filter modal
    activeFilterCount () {
      return this.actualFilteredCount
    },
    // Check if form is valid
    isFormValid () {
      return !this.validationErrors.name &&
             !this.validationErrors.event_delimiter &&
             !this.validationErrors.field_delimiter &&
             !this.validationErrors.regex_string &&
             !this.validationErrors.csv_fields &&
             !this.validationErrors.time_field &&
             !this.validationErrors.time_format &&
             this.item &&
             this.item.name &&
             this.item.type
    },
    // Show modal computed property
    show() {
      return this.modal
    },
    // Check if item has dependencies
    hasDependencies() {
      return this.dependencyDataInputNames.length > 0 || this.dependencyForwarderNames.length > 0
    },
    // Delete confirmation title
    deleteConfirmationTitle() {
      return this.hasDependencies
        ? this.$t('sourceTypes.deleteConfirmation.warningTitle')
        : this.$t('sourceTypes.deleteConfirmation.title')
    },
    // Delete confirmation message
    deleteConfirmationMessage() {
      const name = this.itemToDelete ? this.itemToDelete.name : ''
      return this.$t('sourceTypes.deleteConfirmation.message', { name })
    }
  },
  mounted () {
    // Reset filtered count on component mount
    this.actualFilteredCount = 0
    // Load initial data
    this.loadTableData()

    // Prevent password manager popup by adding hidden dummy inputs
    this.$nextTick(() => {
      const searchInput = this.$el.querySelector('input[type="search"], input[placeholder*="Search"], input[placeholder*="Tìm kiếm"]')
      if (searchInput && searchInput.parentElement) {
        // Create hidden dummy inputs to mislead password managers
        const dummyUsername = document.createElement('input')
        dummyUsername.setAttribute('type', 'text')
        dummyUsername.setAttribute('name', 'fake_username_' + Math.random())
        dummyUsername.style.position = 'absolute'
        dummyUsername.style.top = '-9999px'
        dummyUsername.style.left = '-9999px'
        dummyUsername.setAttribute('tabindex', '-1')
        dummyUsername.setAttribute('aria-hidden', 'true')

        const dummyPassword = document.createElement('input')
        dummyPassword.setAttribute('type', 'password')
        dummyPassword.setAttribute('name', 'fake_password_' + Math.random())
        dummyPassword.style.position = 'absolute'
        dummyPassword.style.top = '-9999px'
        dummyPassword.style.left = '-9999px'
        dummyPassword.setAttribute('tabindex', '-1')
        dummyPassword.setAttribute('aria-hidden', 'true')

        // Insert dummy inputs before the search input
        searchInput.parentElement.insertBefore(dummyUsername, searchInput)
        searchInput.parentElement.insertBefore(dummyPassword, searchInput)
      }
    })
  },
  methods: {
    /**
     * Format date string to readable format: HH:MM:SS DD/MM/YYYY
     */
    formatDate (dateString) {
      if (!dateString || dateString === '0001-01-01T00:00:00Z') return 'N/A'
      const date = new Date(dateString)

      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      return `${hours}:${minutes}:${seconds} ${day}/${month}/${year}`
    },

    /**
     * Format type value with i18n support
     */
    formatType (type) {
      if (type === 'custom') {
        return this.$t('sourceTypes.filter.custom')
      }
      return type
    },

    /**
     * Format type option for dropdown with i18n support
     */
    formatTypeOption (type) {
      if (type === 'custom') {
        return this.$t('sourceTypes.filter.custom')
      }
      return type
    },

    /**
     * Validates source type name and updates validation state
     */
    async validateName() {
      const validation = validateSourceTypeName(
        this.item.name,
        (key, params) => this.$t(key, params),
        this.existingSourceTypes,
        !!this.item.id
      )
      this.validationErrors.name = validation.error
      return validation.isValid
    },

    /**
     * Validates conditional fields based on current form state
     */
    validateConditionalFieldsForm() {
      const validation = validateConditionalFields(this.item, (key, params) => this.$t(key, params))

      // Update individual field errors
      Object.keys(validation.errors).forEach(field => {
        if (this.validationErrors.hasOwnProperty(field)) {
          this.validationErrors[field] = validation.errors[field]
        }
      })

      return validation.isValid
    },

    /**
     * Validates CSV fields
     */
    validateCSVFieldsForm() {
      if (this.item.type === 'csv') {
        const fields = this.fields
        if (fields.length === 0) {
          this.validationErrors.csv_fields = this.$t('sourceTypes.validation.fieldsRequired')
          return false
        }
      }
      this.validationErrors.csv_fields = ''
      return true
    },

    /**
     * Validates all form fields
     */
    async validateForm() {
      this.isValidating = true

      const nameValid = await this.validateName()
      const conditionalValid = this.validateConditionalFieldsForm()
      const csvFieldsValid = this.validateCSVFieldsForm()

      this.isValidating = false

      return nameValid && conditionalValid && csvFieldsValid
    },

    /**
     * Clears validation error for a specific field
     */
    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    /**
     * Loads existing source types for uniqueness validation
     */
    async loadExistingSourceTypes() {
      try {
        const response = await this.$http.get('/api/v1/source-types/names')
        if (response && response.data && response.data.data) {
          this.existingSourceTypes = response.data.data.map(item => item.name)
        }
      } catch (error) {
        console.error('Failed to load existing source types:', error)
        this.existingSourceTypes = []
      }
    },

    /**
     * Applies character limits to form fields
     */
    applyCharacterLimitsToForm() {
      const result = applyCharacterLimits(this.item)

      // Apply truncated values
      Object.keys(result.truncatedValues).forEach(field => {
        if (this.item[field] !== result.truncatedValues[field]) {
          this.item[field] = result.truncatedValues[field]
        }
      })
    },

    /**
     * Handles field blur events with default value restoration
     */
    handleFieldBlur(fieldName, defaultValue = null) {
      // If field is empty and has a default value, restore it
      if ((!this.item[fieldName] || this.item[fieldName].trim() === '') && defaultValue !== null) {
        this.item[fieldName] = defaultValue
      }

      // Apply character limits
      this.applyCharacterLimitsToForm()

      // Validate conditional fields
      this.validateConditionalFieldsForm()
    },

    /**
     * Handles input events with character limit enforcement
     */
    handleFieldInput(fieldName) {
      this.clearFieldError(fieldName)
      this.applyCharacterLimitsToForm()
    },

    /**
     * Updates description character count
     */
    updateDescriptionCharacterCount() {
      this.descriptionCharacterCount = this.item.description ? this.item.description.length : 0
      this.applyCharacterLimitsToForm()
    },

    /**
     * Handles type selection changes and sets default values
     */
    handleTypeChange() {
      // Set default field delimiter for CSV
      if (this.item.type === 'csv' && !this.item.field_delimiter) {
        this.item.field_delimiter = ','
      }

      // Clear validation errors when type changes
      this.validateConditionalFieldsForm()
    },

    escapeNL (str) {
      return str.replace(/\n/g, '\\n')
    },
    unescapeNL (str) {
      return str.replace(/\\n/g, '\n')
    },
    renderFields (item) {
      const fields = item.field_names.split(',')
      return fields.map(f => `${item.field_quote}${f}${item.field_quote}`).join(item.field_delimiter)
    },
    closeModal () {
      this.modal = false
    },
    /**
     * Handle modal close event - prevent closing on outside click
     */
    handleModalClose(value) {
      // Prevent modal from closing on outside click or ESC
      // Only allow closing through close button (X) or Cancel button
      // Do nothing - modal will only close through closeModal()
    },
    fieldAdd () {
      if (!this.field.name || this.field.name.trim() === '') {
        return
      }

      const names = this.fields.map(x => x.name)
      if (names.length === uniq(names.concat(this.field.name.trim())).length) {
        return
      }

      this.item.field_names = names.concat(this.field.name.trim()).join(',')
      this.field = {
        name: ''
      }

      // Revalidate CSV fields after adding
      this.validateCSVFieldsForm()
    },
    fieldRemove (idx) {
      const names = this.fields.map(x => x.name)
      names.splice(idx, 1)
      this.item.field_names = names.join(',')

      // Revalidate CSV fields after removing
      this.validateCSVFieldsForm()
    },
    async submit () {
      // Apply character limits before validation
      this.applyCharacterLimitsToForm()

      // Validate form
      const isValid = await this.validateForm()
      if (!isValid) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('users.validation.formHasErrors')
        })
        return
      }

      if (this.item.id) {
        this.update()
        return
      }
      this.store()
    },
    async create () {
      // Load existing source types for validation
      await this.loadExistingSourceTypes()

      this.isEditMode = false
      this.item = null
      this.modal = true
    },
    async edit (item) {
      // Check if this is a ParserV2 source type
      const parserv2Types = ['sophos', 'f5_asm', 'fortinet', 'vcenter']
      if (item.parser_type && parserv2Types.includes(item.parser_type)) {
        this.parserV2EditItem = JSON.parse(JSON.stringify(item))
        this.isParserV2EditMode = true
        this.showParserV2Wizard = true
        return
      }

      // Load existing source types for validation
      await this.loadExistingSourceTypes()

      this.isEditMode = true
      this.item = JSON.parse(JSON.stringify(item))
      this.modal = true
    },

    /**
     * Handle form success event
     */
    handleFormSuccess() {
      this.loadTableData()
    },

    /**
     * Load table data from API
     */
    async loadTableData() {
      this.isTableLoading = true

      const params = {
        filter: this.searchQuery,
        sort: this.sortBy,
        order: this.sortOrder,
        page: this.currentPage,
        limit: this.itemsPerPage,
        // Comma-separated list of selected formats
        types: (this.currentFilters.types || []).join(',')
      }

      try {
        const response = await this.$http.get('/api/v1/source-types', { params })
        if (response && response.data && response.data.data) {
          this.tableData = response.data.data.items || []
          this.totalItems = response.data.data.total || 0
        }
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          error
        })
      } finally {
        this.isTableLoading = false
      }
    },
    /**
     * Handle search event from DataTable
     */
    handleSearch(query) {
      this.searchQuery = query
      this.currentPage = 1
      this.loadTableData()
    },
    /**
     * Handle sort event from DataTable
     */
    handleSort(sortData) {
      this.sortBy = sortData.by
      this.sortOrder = sortData.order
      this.loadTableData()
    },
    /**
     * Handle page change event from DataTable
     */
    handlePageChange(page) {
      this.currentPage = page
      this.loadTableData()
    },
    /**
     * Handle page size change event from DataTable
     */
    handlePageSizeChange(pageSize) {
      this.itemsPerPage = pageSize
      this.currentPage = 1
      this.loadTableData()
    },
    store () {
      return this.$http.post('/api/v1/source-types', this.item).then(body => {
        if (!body || !body.data) {
          return
        }

        this.closeModal()
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message
        })

        this.loadTableData()
      })
    },
    update () {
      return this.$http.patch(`/api/v1/source-types/${this.item.id}`, this.item).then(body => {
        if (!body || !body.data) {
          return
        }

        this.closeModal()
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message
        })

        this.loadTableData()

      })
    },
    /**
     * Apply filters from the filter modal
     */
    applyFilters (filters) {
      this.currentFilters = {
        types: [...(filters.types || [])]
      }

      // Use the result count from the filter modal
      this.actualFilteredCount = filters.resultCount || 0

      if (this.$refs.table && this.$refs.table.loadItems) {
        this.loadTableData()
      }
    },
    /**
     * Clear all filters
     */
    clearFilters () {
      this.currentFilters = {
        types: []
      }
      this.actualFilteredCount = 0
      if (this.$refs.table && this.$refs.table.loadItems) {
        this.loadTableData()
      }
    },
    async destroy (item) {
      this.itemToDelete = item

      // Check dependencies before showing confirmation
      try {
        const response = await this.$http.get(`/api/v1/source-types/${item.id}/dependencies`)
        if (response && response.data && response.data.data) {
          this.dependencyDataInputNames = response.data.data.data_input_names || []
          this.dependencyForwarderNames = response.data.data.forwarder_names || []
        }
      } catch (error) {
        console.error('Failed to check dependencies:', error)
        this.dependencyDataInputNames = []
        this.dependencyForwarderNames = []
      }

      this.showDeleteConfirmation = true
    },

    /**
     * Handle the confirmation of source type deletion
     */
    handleDeleteConfirm() {
      if (!this.itemToDelete) {
        return
      }

      return this.$http.delete(`/api/v1/source-types/${this.itemToDelete.id}`).then(body => {
        if (!body || !body.data) {
          return
        }

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('sourceTypes.messages.deleteSuccess')
        })

        this.showDeleteConfirmation = false
        this.itemToDelete = null
        // Clear dependency data
        this.dependencyDataInputNames = []
        this.dependencyForwarderNames = []
        this.loadTableData()
      }).catch(error => {
        this.showDeleteConfirmation = false

        if (error.response && error.response.status === 409) {
          this.handleDependencyError(error.response.data.message)
        } else {
          // Handle other errors normally
          this.$store.dispatch('NOTIFY', {
            type: 'error',
            text: this.$t('sourceTypes.messages.deleteError')
          })
        }
      })
    },

    /**
     * Handle dependency error by parsing the message and showing the warning dialog
     */
    handleDependencyError(message) {
      // Parse the Vietnamese error message to extract dependency names
      const dataInputMatch = message.match(/DataInput:\s*([^,]+(?:,\s*[^,]+)*)/i)
      const forwarderMatch = message.match(/Forwarder:\s*([^,]+(?:,\s*[^,]+)*)/i)

      this.dependencyDataInputNames = dataInputMatch
        ? dataInputMatch[1].split(',').map(name => name.trim()).filter(name => name)
        : []

      this.dependencyForwarderNames = forwarderMatch
        ? forwarderMatch[1].split(',').map(name => name.trim()).filter(name => name)
        : []

      this.showDependencyWarning = true
    },

    /**
     * Cancel the delete operation
     */
    cancelDelete() {
      this.showDeleteConfirmation = false
      this.itemToDelete = null
      // Clear dependency data
      this.dependencyDataInputNames = []
      this.dependencyForwarderNames = []
    },

    /**
     * Close the dependency warning dialog
     */
    closeDependencyWarning() {
      this.showDependencyWarning = false
      this.dependencyDataInputNames = []
      this.dependencyForwarderNames = []
    },

    /**
     * Handle wizard success - reload table data
     */
    handleWizardSuccess() {
      this.loadTableData()
    },

    /**
     * Open ParserV2 Wizard for creating new source type
     */
    openParserV2Wizard() {
      this.parserV2EditItem = null
      this.isParserV2EditMode = false
      this.showParserV2Wizard = true
    },

    /**
     * Handle ParserV2 Wizard close - reset state
     */
    handleParserV2WizardClose(value) {
      this.showParserV2Wizard = value
      if (!value) {
        this.isParserV2EditMode = false
        this.parserV2EditItem = null
      }
    }
  }
}
</script>
<style scoped>
pre {
  padding: 0.5em;
}

/* Settings column styling */
.settings-tabs {
  font-size: 0.75rem;
}

.settings-pre {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  margin: 0;
  max-height: 80px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: #f5f5f5;
  border-radius: 4px;
}

/* Dark theme support for settings pre */
.theme-dark .settings-pre {
  background-color: #2b2b2b;
  color: #e0e0e0;
}

.truncate-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
